// frontend/src/apps/los/store/losStore.ts
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

/**
 * LOS App-scoped Store
 * 
 * Manages state for all LOS journey components (PersonalInfo, BusinessInfo, LoanOffer, etc.)
 * Sync pattern: Components write → Store → debounced → Backend API → Database
 * 
 * Provides:
 * 1. Component-isolated state (individual, business, loan, document)
 * 2. Pending changes tracking (to show "saving..." UI)
 * 3. Debounced batch writes (1-2 sec delay to avoid DB hammering)
 * 4. Orchestrator integration (batch saves multiple component changes)
 */

export interface IndividualData {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth: string;
  gender: string;
  address?: string;
  pincode?: string;
}

export interface BusinessData {
  business_name: string;
  nature_of_business: string;
  business_activity: string;
  business_role: string;
  other_role: string;
  gst_number: string;
  date_of_incorporation: string;
  company_pan_number: string;
  business_constitution_type: string;
  udyam_number: string;
  cin_llpin_number: string;
  registered_address_line1?: string;
  registered_address_line2?: string;
  registered_address_line3?: string;
  registered_address_landmark?: string;
  registered_address_pincode?: string;
  business_address_line1?: string;
  business_address_line2?: string;
  business_address_line3?: string;
  business_address_landmark?: string;
  business_address_pincode?: string;
}

export interface LoanData {
  amount: number;
  tenure: number;
  interest_rate?: number;
  repayment_method?: string;
}

export interface DocumentData {
  [key: string]: any; // Document references, etc.
}

export interface LosStoreState {
  // Data sections
  individual: Partial<IndividualData>;
  business: Partial<BusinessData>;
  loan: Partial<LoanData>;
  documents: Partial<DocumentData>;
  
  // Tracking pending changes per field
  pendingChanges: Set<string>;
  
  // Save status
  isSaving: boolean;
  lastSavedAt: Date | null;
  saveError: string | null;
  
  // Setters
  setIndividual: (data: Partial<IndividualData>) => void;
  setBusiness: (data: Partial<BusinessData>) => void;
  setLoan: (data: Partial<LoanData>) => void;
  setDocuments: (data: Partial<DocumentData>) => void;
  
  // Change tracking
  markFieldChanged: (fieldName: string) => void;
  clearPendingChanges: () => void;
  
  // Save callbacks
  setSaving: (isSaving: boolean) => void;
  setSaveSuccess: () => void;
  setSaveError: (error: string) => void;
  
  // Orchestrated batch save (called by orchestrator)
  getPendingChanges: () => {
    individual?: Partial<IndividualData>;
    business?: Partial<BusinessData>;
    loan?: Partial<LoanData>;
    documents?: Partial<DocumentData>;
  };
  
  // Reset store
  reset: () => void;
}

// Default empty state
const defaultState = {
  individual: {},
  business: {},
  loan: {},
  documents: {},
  pendingChanges: new Set<string>(),
  isSaving: false,
  lastSavedAt: null,
  saveError: null,
};

let debounceTimer: NodeJS.Timeout | null = null;

export const useLosStore = create<LosStoreState>()(
  immer((set, get) => {
    // Debounced orchestrator sync function
    const debouncedSync = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      
      debounceTimer = setTimeout(async () => {
        const state = get();
        if (state.pendingChanges.size === 0) return;
        
        const pendingData = state.getPendingChanges();
        
        // Trigger orchestrator sync (implemented in losService)
        try {
          set({ isSaving: true });
          await syncToBackend(pendingData);
          set({ lastSavedAt: new Date(), pendingChanges: new Set() });
        } catch (error) {
          set({ saveError: (error as Error).message });
        } finally {
          set({ isSaving: false });
        }
      }, 1500); // 1.5 second debounce
    };

    return {
      ...defaultState,
      
      setIndividual: (data: Partial<IndividualData>) =>
        set((state) => {
          state.individual = { ...state.individual, ...data };
          debouncedSync();
        }),
      
      setBusiness: (data: Partial<BusinessData>) =>
        set((state) => {
          state.business = { ...state.business, ...data };
          debouncedSync();
        }),
      
      setLoan: (data: Partial<LoanData>) =>
        set((state) => {
          state.loan = { ...state.loan, ...data };
          debouncedSync();
        }),
      
      setDocuments: (data: Partial<DocumentData>) =>
        set((state) => {
          state.documents = { ...state.documents, ...data };
          debouncedSync();
        }),
      
      markFieldChanged: (fieldName: string) =>
        set((state) => {
          state.pendingChanges.add(fieldName);
        }),
      
      clearPendingChanges: () =>
        set((state) => {
          state.pendingChanges.clear();
        }),
      
      setSaving: (isSaving: boolean) =>
        set({ isSaving }),
      
      setSaveSuccess: () =>
        set({ lastSavedAt: new Date(), saveError: null, pendingChanges: new Set() }),
      
      setSaveError: (error: string) =>
        set({ saveError: error, isSaving: false }),
      
      getPendingChanges: () => {
        const state = get();
        const changes: ReturnType<LosStoreState['getPendingChanges']> = {};
        
        if (state.pendingChanges.size === 0) return changes;
        
        // Only include sections that have pending changes
        if (
          state.pendingChanges.has('first_name') ||
          state.pendingChanges.has('last_name') ||
          state.pendingChanges.has('phone') ||
          state.pendingChanges.has('email') ||
          state.pendingChanges.has('date_of_birth') ||
          state.pendingChanges.has('gender')
        ) {
          changes.individual = state.individual;
        }
        
        if (
          state.pendingChanges.has('business_name') ||
          state.pendingChanges.has('nature_of_business') ||
          state.pendingChanges.has('business_activity') ||
          state.pendingChanges.has('business_role') ||
          state.pendingChanges.has('other_role') ||
          state.pendingChanges.has('gst_number') ||
          state.pendingChanges.has('date_of_incorporation') ||
          state.pendingChanges.has('company_pan_number') ||
          state.pendingChanges.has('business_constitution_type') ||
          state.pendingChanges.has('udyam_number') ||
          state.pendingChanges.has('cin_llpin_number') ||
          state.pendingChanges.has('registered_address_line1') ||
          state.pendingChanges.has('registered_address_line2') ||
          state.pendingChanges.has('registered_address_line3') ||
          state.pendingChanges.has('registered_address_landmark') ||
          state.pendingChanges.has('registered_address_pincode') ||
          state.pendingChanges.has('business_address_line1') ||
          state.pendingChanges.has('business_address_line2') ||
          state.pendingChanges.has('business_address_line3') ||
          state.pendingChanges.has('business_address_landmark') ||
          state.pendingChanges.has('business_address_pincode')
        ) {
          changes.business = state.business;
        }
        
        if (
          state.pendingChanges.has('amount') ||
          state.pendingChanges.has('tenure') ||
          state.pendingChanges.has('interest_rate')
        ) {
          changes.loan = state.loan;
        }
        
        return changes;
      },
      
      reset: () => set(defaultState),
    };
  })
);

/**
 * Sync component data to backend via orchestrator
 * This is called after debounce timeout
 * 
 * Orchestrator batches multiple component changes:
 * POST /api/orchestrator/
 * {
 *   "components": [
 *     "LOS_SetIndividualName_012",
 *     "LOS_SetDateOfBirth_014",
 *     "LOS_SetGender_016"
 *   ],
 *   "data": { ... }
 * }
 */
async function syncToBackend(pendingData: any) {
  // This function calls the backend orchestrator
  // Implemented in losService.ts
  const { syncLosData } = await import('../services/losService');
  await syncLosData(pendingData);
}

/**
 * Hook to load initial data from backend when component mounts
 * Usage: useEffect(() => { loadInitialData() }, [])
 */
export async function loadLosInitialData() {
  // Fetch data for all components in a page via orchestrator
  // POST /api/orchestrator/
  // {
  //   "components": [
  //     "LOS_GetIndividualName_011",
  //     "LOS_GetDateOfBirth_013",
  //     "LOS_GetGender_015"
  //   ]
  // }
  const { getLosData } = await import('../services/losService');
  const data = await getLosData();
  
  const store = useLosStore.getState();
  if (data.individual) store.setIndividual(data.individual);
  if (data.business) store.setBusiness(data.business);
  if (data.loan) store.setLoan(data.loan);
}
